import React, { useState } from 'react';
import { validateEmail } from '@tesla/validation';
import { arrayOf, bool, shape, string } from 'prop-types';
import { useIntl } from 'react-intl';
import { FormFieldset, FormLegend } from '@tesla/design-system-react';
import { FormField } from 'informed';
import XRegExp from 'xregexp';
import { formatDate } from '@tesla/intl-date-time';
import messages from './messages';
import { useTCLFormApi } from '../../../hooks';
import formStyle from '../../JobApplicationForm/style.module.scss';
import style from './style.module.scss';
import eeoMessages from './eeo-v1.json';
import { Disclaimer } from '../../index';
import { getLegalCopyKey, isInternalEmail } from '../../../utils/common';

const PersonalLegalEEOSection = ({ fields, optionalResumeValidation }) => {
  const intl = useIntl();
  const { formatMessage, locale } = intl;
  const formHelper = useTCLFormApi();

  const validateLegalAcknowledgment = (value) =>
    value !== true && formatMessage(messages.AcknowledgmentRequiredLegalMessage);
  const [hasReadDisclaimer, setHasReadDisclaimer] = useState(false);

  const validateEEOAcknowledgment = (value) =>
    value !== true ? formatMessage(messages.AcknowledgmentRequiredMessage) : undefined;

  const validateAlphaCharacters = (value) => {
    const pattern = XRegExp("^[\\p{L} .’'-]+$", 'gu');
    return pattern.test(value);
  };

  const validateEmailAddress = (value) => {
    if (value) {
      if (validateEmail(value) === true) {
        if (isInternalEmail(value) === true)
          return (
            formHelper?.schema?.formSchema?.properties?.email?.errorMessage?.internalEmail ??
            'invalid'
          );
      } else {
        return (
          formHelper?.schema?.formSchema?.properties?.email?.errorMessage?.invalidEmail ?? 'invalid'
        );
      }
    }
    return undefined;
  };

  const validateOptionalResume = (value, values) => {
    const { resume, evidenceOfExcellence, profileLinks } = values.personallegaleeo;
    const errorMessage = formatMessage(messages.OptionalResumeValidationMessage);
    const hasProfileLink = () => {
      if (typeof profileLinks === 'string') {
        return profileLinks.length > 0;
      }
      return profileLinks && profileLinks.filter((item) => item.type && item.link).length > 0;
    };
    return !resume && !evidenceOfExcellence && !hasProfileLink() ? errorMessage : undefined;
  };

  const legalImmigrationLabel =
    fields.legalImmigrationSponsorship && formHelper.field(fields.legalImmigrationSponsorship)
      ? formHelper.field(fields.legalImmigrationSponsorship).schema.properties
          .legalImmigrationSponsorship.title
      : '';

  /**
   * Ensures phone number is correctly validated as required.
   *
   * @param value
   *   The phone value object.
   * @returns {boolean|string}
   */
  const validateRequiredPhone = (value) =>
    value.number == null ? formatMessage(messages.PhoneInputValidationMessage) : undefined;

  const currentDate = formatDate(new Date(), locale, { dateStyle: 'long' });

  const legalCopyKey = getLegalCopyKey(formHelper.applicationType, locale);

  const getFieldSchema = (field) => {
    const { name, schema } = formHelper.field(field);
    return {
      name,
      schema: schema.properties[name],
    };
  };

  return (
    <>
      <FormFieldset className={`${formStyle.FormSection} tds-form-layout--2col`}>
        {fields.firstName && (
          <FormField
            {...formHelper.field(fields.firstName)}
            validate={(value) =>
              !value || !validateAlphaCharacters(value)
                ? formatMessage(messages.FirstNameInputValidationMessage)
                : undefined
            }
          />
        )}
        {fields.lastName && (
          <FormField
            {...formHelper.field(fields.lastName)}
            validate={(value) =>
              !value || !validateAlphaCharacters(value)
                ? formatMessage(messages.LastNameInputValidationMessage)
                : undefined
            }
          />
        )}
        {fields.middleName && <FormField {...formHelper.field(fields.middleName)} />}
        {fields.preferredName && <FormField {...formHelper.field(fields.preferredName)} />}
        {fields.phone && (
          <FormField
            {...formHelper.field(fields.phone)}
            className={style.PhoneInput}
            // errorMessage={{
            //   required: formatMessage(messages.PhoneInputValidationMessage),
            // }}
            // generateValidationMessage={() => '$pattern'}
            validate={validateRequiredPhone}
          />
        )}
        {fields.phoneType && (
          <FormField
            {...formHelper.field(fields.phoneType)}
            errorMessage={formatMessage(messages.PhoneInputValidationMessage)}
          />
        )}
        {fields.email && (
          <FormField {...formHelper.field(fields.email)} validate={validateEmailAddress} />
        )}
        {fields.address && <FormField {...formHelper.field(fields.address)} />}
      </FormFieldset>

      {optionalResumeValidation && (
        <h4
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: formatMessage(messages.OptionalResumeMessage) }}
        />
      )}
      {optionalResumeValidation && fields.profileLinks && (
        <FormFieldset className={`${formStyle.FormSection} tds-form-layout--2col`}>
          <FormField
            {...formHelper.field(fields.profileLinks)}
            initialValue={
              formHelper.field(fields.profileLinks).schema.properties.profileLinks.type !== 'string'
                ? [{ type: '', link: '' }]
                : ''
            }
            max="1"
            validate={optionalResumeValidation && validateOptionalResume}
          />
        </FormFieldset>
      )}
      {(fields.resume || fields.evidenceOfExcellence) && (
        <FormFieldset className={formStyle.FormSection}>
          {fields.resume && (
            <div className={`${formStyle.FormFieldHalfWidth}`}>
              <FormField
                {...formHelper.field(fields.resume)}
                validate={optionalResumeValidation && validateOptionalResume}
              />
            </div>
          )}
          {fields.evidenceOfExcellence && (
            <FormField
              {...formHelper.field(fields.evidenceOfExcellence)}
              validate={optionalResumeValidation && validateOptionalResume}
            />
          )}
        </FormFieldset>
      )}
      {fields.other && (
        <FormFieldset className={formStyle.FormSection}>
          {fields.other.map((field) => (
            <div className={`${formStyle.FormFieldHalfWidth}`}>
              <FormField {...formHelper.field(field)} />
            </div>
          ))}
        </FormFieldset>
      )}
      <FormFieldset
        className={formStyle.FormSection}
        legend={formHelper.field(fields.legalLegend).schema.properties.legalLegend.text}
        legendProps={{ looksLike: 'h3' }}
      >
        {fields.legalImmigrationSponsorship &&
          formHelper.field(fields.legalImmigrationSponsorship).schema.properties
            .legalImmigrationSponsorship['ui:control'] === 'switch_toggle' && (
            <div className={`${formStyle.FormFieldHalfWidth}`}>
              <div className="tds-form-item">
                <div className="tds-form-label tds-form-label-wrap">
                  <label className="tds-form-label" htmlFor="legalImmigrationSponsorship">
                    <span className="tds-form-label-text">{legalImmigrationLabel}</span>
                  </label>
                </div>
                <div className="tds-form-input tds-form-input-wrap--switchtoggle">
                  <FormField
                    id="legalImmigrationSponsorship"
                    {...formHelper.field(fields.legalImmigrationSponsorship)}
                  />
                </div>
              </div>
            </div>
          )}
        {fields.legalOther.map((field) => {
          const schemaField = getFieldSchema(field);
          if (schemaField.schema['ui:control'] === 'switch_toggle') {
            return (
              <div className={`${formStyle.FormFieldHalfWidth}`}>
                <div className="tds-form-item">
                  <div className="tds-form-label tds-form-label-wrap">
                    <span className="tds-form-label">
                      <span className="tds-form-label-text">{schemaField.schema.title}</span>
                    </span>
                  </div>
                  <div className="tds-form-input tds-form-input-wrap--switchtoggle">
                    <FormField {...formHelper.field(field)} />
                  </div>
                </div>
              </div>
            );
          }
          return <FormField {...formHelper.field(field)} />;
        })}
      </FormFieldset>

      {fields.legalAcknowledgment && (
        <FormFieldset className={`${formStyle.FormSection}`}>
          <div className={formStyle.Intro}>
            <FormLegend text={formatMessage(messages.AcknowledgmentHeading)} variant="h5" />
            <p
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: formatMessage(messages[legalCopyKey]),
              }}
            />
          </div>
          <FormField
            {...formHelper.field(fields.legalAcknowledgment)}
            validate={validateLegalAcknowledgment}
            required
          />
        </FormFieldset>
      )}
      {fields.eeoAcknowledgment && (
        <FormFieldset className={`${formStyle.FormSection}`}>
          <Disclaimer hasRead={setHasReadDisclaimer}>
            <FormLegend text={formatMessage(messages.AcknowledgmentHeading)} variant="h5" />
            <p
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: formatMessage(messages[legalCopyKey]),
              }}
            />
            <h3 className="tds-text--h5">{eeoMessages.Intro.Heading}</h3>
            <p>{eeoMessages.Intro.Copy}</p>

            <h3 className="tds-text--h5">{eeoMessages.Definitions.RaceEthnicity.Heading}</h3>
            {/* eslint-disable-next-line react/no-danger */}
            <div dangerouslySetInnerHTML={{ __html: eeoMessages.Definitions.RaceEthnicity.Copy }} />
            <h3 className="tds-text--h5">{eeoMessages.Definitions.VeteranStatus.Heading}</h3>
            {/* eslint-disable-next-line react/no-danger */}
            <div dangerouslySetInnerHTML={{ __html: eeoMessages.Definitions.VeteranStatus.Copy }} />

            <h3 className="tds-text--h5">{eeoMessages.DisabilityStatus.Heading}</h3>
            {/* eslint-disable-next-line react/no-danger */}
            <div dangerouslySetInnerHTML={{ __html: eeoMessages.DisabilityStatus.Copy }} />

            <h3 className="tds-text--h5">{eeoMessages.ReasonableAccommodationStatement.Heading}</h3>
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: eeoMessages.ReasonableAccommodationStatement.Copy,
              }}
            />
            <h3 className="tds-text--h5">{eeoMessages.OFCCPNotice.Heading}</h3>
            {/* eslint-disable-next-line react/no-danger */}
            <div dangerouslySetInnerHTML={{ __html: eeoMessages.OFCCPNotice.Copy }} />
          </Disclaimer>
          {!hasReadDisclaimer && (
            <FormField
              {...{
                ...formHelper.field(fields.eeoAcknowledgment),
                schema: {
                  ...formHelper.field(fields.eeoAcknowledgment).schema,
                  properties: {
                    ...formHelper.field(fields.eeoAcknowledgment).schema.properties,
                    eeoAcknowledgment: {
                      ...formHelper.field(fields.eeoAcknowledgment).schema.properties
                        .eeoAcknowledgment,
                      'ui:props': {
                        ...formHelper.field(fields.eeoAcknowledgment).schema.properties
                          .eeoAcknowledgment['ui:props'],
                        disabled: true,
                      },
                    },
                  },
                },
              }}
            />
          )}
          {hasReadDisclaimer && (
            <FormField
              {...formHelper.field(fields.eeoAcknowledgment)}
              validate={validateEEOAcknowledgment}
            />
          )}
        </FormFieldset>
      )}
      <FormFieldset className={`${formStyle.FormSection} tds-form-layout--2col`}>
        {fields.dob && <FormField {...formHelper.field(fields.dob)} />}
        {fields.last4ssn && <FormField {...formHelper.field(fields.last4ssn)} />}
        <FormField {...formHelper.field(fields.eeoGender)} />
        <FormField {...formHelper.field(fields.eeoVeteranStatus)} />

        <FormField {...formHelper.field(fields.eeoRaceEthnicity)} />
        <FormField {...formHelper.field(fields.eeoDisabilityStatus)} />

        <FormField {...formHelper.field(fields.eeoDisabilityStatusName)} />
        <div className="tds-form-item">
          <div className="tds-form-label">Date</div>
          <div className={`tds-form-input tds-form-input--default ${style.DateOverride}`}>
            <div className="tds-form-input-text tds-form-input--readonly">{currentDate}</div>
          </div>
        </div>
      </FormFieldset>
    </>
  );
};

PersonalLegalEEOSection.propTypes = {
  optionalResumeValidation: bool,
  fields: shape({
    firstName: string,
    lastName: string,
    middleName: string,
    preferredName: string,
    phoneType: string,
    phone: string,
    email: string,
    address: string,
    resume: string,
    profileLinks: string,
    evidenceOfExcellence: string,
    other: arrayOf(string),
    legalOther: arrayOf(string),
    legalAcknowledgment: string,
    legalAcknowledgmentName: string,
    legalImmigrationSponsorship: string,
    eeoAcknowledgment: string,
    eeoGender: string,
    eeoRaceEthnicity: string,
    eeoVeteranStatus: string,
    eeoDisabilityStatus: string,
    eeoDisabilityStatusName: string,
    eeoDisabilityStatusDate: string,
  }),
};

PersonalLegalEEOSection.defaultProps = {
  optionalResumeValidation: false,
  fields: {
    firstName: '',
    lastName: '',
    middleName: '',
    preferredName: '',
    phoneType: '',
    phone: '',
    email: '',
    address: '',
    resume: '',
    profileLinks: '',
    evidenceOfExcellence: '',
    legalImmigrationSponsorship: '',
    other: [],
    legalOther: [],
  },
};

export default PersonalLegalEEOSection;
